import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiPackage,
  FiTruck,
  FiCheckCircle,
  FiClock,
  FiSearch,
  FiPhone,
  FiMail,
  FiAlertCircle
} from 'react-icons/fi';
import Breadcrumb from '../components/common/Breadcrumb';
import ErrorMessage from '../components/common/ErrorMessage';
import api from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';

const TrackOrderPage = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const statusSteps = [
    { key: 'confirmed', label: 'Order Confirmed', icon: FiCheckCircle },
    { key: 'processed', label: 'Processed', icon: FiPackage },
    { key: 'shipped', label: 'Shipped', icon: FiTruck },
    { key: 'out_for_delivery', label: 'Out for Delivery', icon: FiTruck },
    { key: 'delivered', label: 'Delivered', icon: FiCheckCircle }
  ];

  const getStepKey = (status) => {
    if (status === 'processing') return 'processed';
    return status;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!orderNumber.trim() || !email.trim()) {
      setError('Please enter both order number and email');
      return;
    }

    setLoading(true);
    setError(null);
    setTrackingInfo(null);

    try {
      const response = await api.get('/orders/track', {
        params: { orderNumber, email }
      });
      setTrackingInfo(response);
    } catch (err) {
      const message = err.response?.data?.message || 'Order not found. Please check your order number and email.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'var(--primary-color)';
      case 'shipped':
      case 'out_for_delivery': return 'var(--secondary-color)';
      case 'processed':
      case 'processing':
      case 'confirmed': return '#f59e0b';
      default: return '#9ca3af';
    }
  };

  const getStatusIcon = (status) => {
    const stepKey = getStepKey(status);
    const StepIcon = statusSteps.find(s => s.key === stepKey)?.icon || FiClock;
    return StepIcon;
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="border-b">
        <div className="container py-4">
          <Breadcrumb
            items={[
              { label: 'Home', path: '/' },
              { label: 'Track Order', path: '/track-order' }
            ]}
          />
        </div>
      </div>

      <section
        className="py-16"
        style={{ background: 'var(--primary-color)' }}
      >
        <div className="container-custom text-center text-white">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Track Your Order</h1>
          <p className="text-xl max-w-2xl mx-auto opacity-90">
            Enter your order number and email to track your shipment
          </p>
        </div>
      </section>

      <div className="container-custom py-12">
        {!trackingInfo ? (
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order Number
                  </label>
                  <input
                    type="text"
                    value={orderNumber}
                    onChange={(e) => setOrderNumber(e.target.value)}
                    placeholder="e.g., ARSHI123456"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': 'var(--primary-color)' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': 'var(--primary-color)' }}
                  />
                </div>

                {error && (
                  <ErrorMessage type="error" message={error} />
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center"
                  style={{
                    backgroundColor: 'var(--primary-color)',
                    '--hover-color': 'var(--hover-primary)'
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--hover-primary)'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'var(--primary-color)'}
                >
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" color="white" className="mr-2" />
                      Tracking...
                    </>
                  ) : (
                    <>
                      <FiSearch className="mr-2" />
                      Track Order
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  Need help?{' '}
                  <Link to="/contact" style={{ color: 'var(--primary-color)' }} className="hover:underline">
                    Contact Support
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-4">Where to find your order number?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Your order number can be found in:
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center">
                  <FiMail className="mr-2" style={{ color: 'var(--primary-color)' }} size={14} />
                  Order confirmation email
                </li>
                <li className="flex items-center">
                  <FiPackage className="mr-2" style={{ color: 'var(--primary-color)' }} size={14} />
                  Order invoice
                </li>
                <li className="flex items-center">
                  <FiPhone className="mr-2" style={{ color: 'var(--primary-color)' }} size={14} />
                  SMS confirmation
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200">
              <div className="flex flex-wrap items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Order Number</p>
                  <h2 className="text-2xl font-bold text-gray-900">{trackingInfo.orderNumber}</h2>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Order Date</p>
                  <p className="font-semibold">{new Date(trackingInfo.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 mb-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-8">Order Status</h3>

              <div className="relative">
                <div className="absolute top-5 left-0 w-full h-1 bg-gray-200">
                  <div
                    className="h-full transition-all duration-500"
                    style={{
                      width: `${(statusSteps.findIndex(s => s.key === getStepKey(trackingInfo.orderStatus)) + 1) * (100 / statusSteps.length)}%`,
                      backgroundColor: 'var(--primary-color)'
                    }}
                  />
                </div>

                <div className="relative flex justify-between">
                  {statusSteps.map((step, index) => {
                    const StepIcon = step.icon;
                    const stepKey = getStepKey(trackingInfo.orderStatus);
                    const isCompleted = statusSteps.findIndex(s => s.key === stepKey) >= index;
                    const isCurrent = step.key === stepKey;

                    return (
                      <div key={step.key} className="text-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2 ${isCompleted ? 'text-white' : 'bg-gray-200 text-gray-400'}`}
                          style={{
                            backgroundColor: isCompleted ? 'var(--primary-color)' : undefined,
                            ringColor: isCurrent ? 'var(--primary-color)' : undefined
                          }}
                        >
                          <StepIcon size={18} />
                        </div>
                        <p className={`text-xs font-medium ${isCompleted ? 'text-gray-900' : 'text-gray-400'}`}>
                          {step.label}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {trackingInfo.estimatedDelivery && (
                <div
                  className="mt-8 p-4 rounded-lg"
                  style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
                >
                  <p className="text-sm">
                    Estimated Delivery: <span className="font-semibold">
                      {new Date(trackingInfo.estimatedDelivery).toLocaleDateString('en-PK', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {trackingInfo.shippingAddress && (
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Shipping Address</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p className="font-medium">{trackingInfo.shippingAddress.name}</p>
                    <p>{trackingInfo.shippingAddress.address}</p>
                    <p>{trackingInfo.shippingAddress.city}, {trackingInfo.shippingAddress.province}</p>
                    <p>Phone: {trackingInfo.shippingAddress.phone}</p>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Delivery Information</h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-gray-500">Carrier</p>
                    <p className="font-medium">{trackingInfo.carrier || 'Standard'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Tracking Number</p>
                    <p className="font-medium">{trackingInfo.trackingNumber || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
              <div className="space-y-3">
                {trackingInfo.items?.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                    <span className="text-gray-900">{item.productName || 'Product'}</span>
                    <span className="text-sm text-gray-500">Qty: {item.quantity}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => setTrackingInfo(null)}
                className="flex-1 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                Track Another Order
              </button>
              <Link
                to="/contact"
                className="flex-1 py-3 text-white rounded-lg font-medium text-center hover:opacity-90 transition-opacity"
                style={{ backgroundColor: 'var(--primary-color)' }}
              >
                Need Help?
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrderPage;